import Logger from './logger';

const TEST_VIDEO = document.createElement('video');
const TEST_AUDIO = document.createElement('audio');
const NO_CHROME_PATTERN = /(samsungbrowser\/|firefox\/|opr\/|edg\/|edge\/|fxios)/i;


export default class Env {

  static #filterVersion(pattern, minor) {
    if (navigator.userAgent !== null) {
      const versionArray = navigator.userAgent.match(pattern);
      if (minor && Array.isArray(versionArray) && typeof versionArray[2] !== 'undefined') {
        return parseInt(versionArray[2], 10);
      } else if (Array.isArray(versionArray) && typeof versionArray[1] !== 'undefined') {
        return parseInt(versionArray[1], 10);
      }
    }
    return -1;
  }

  static get #html5Video() {
    return typeof TEST_VIDEO.canPlayType !== 'undefined';
  }

  static get #html5Audio() {
    return typeof TEST_AUDIO.canPlayType !== 'undefined';
  }

  static get userAgent() {
    return navigator.userAgent || 'null';
  }

  static get platform() {
    return navigator.platform || 'null';
  }

  static get protocol() {
    if (location.protocol) {
      return location.protocol.toLowerCase();
    }
    return null;
  }

  static get primaryPointerIsTouchScreen() {
    try {
      if (typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches) {
        return true;
      }
      return false;
    } catch (warning) {
      console.warn(warning);
      return false;
    }
  }

  static get isIos() {
    const iosPattern = /(ipad|iphone|ipod)/i;
    const iosVersionPattern = /os\s+(\d+)/i;
    if (Env.primaryPointerIsTouchScreen && iosPattern.test(Env.userAgent)) {
      return [true, Env.#filterVersion(iosVersionPattern, false)];
    }
    return [false, -1];
  }

  static get isIpadOS() {
    const macPlatformPattern = /macintel/i;
    if (!Env.isIos[0] && Env.primaryPointerIsTouchScreen && macPlatformPattern.test(Env.platform) &&
      Env.devicePixelRatio > 1 && navigator.maxTouchPoints && navigator.maxTouchPoints >= 2) {
      return true;
    }
    return false;
  }

  static get isMacOS() {
    const macosPattern = /(macintosh|mac\s+os)/i;
    if (!Env.isIos[0] && macosPattern.test(Env.userAgent) && !Env.isIpadOS) {
      return true;
    }
    return false;
  }

  static get isSafari() {
    const safariPattern = /safari\/[.0-9]*/i;
    const noSafariPattern = /(chrome|chromium|crios)/i;
    const safariVersionPattern = /version\/(\d+)/i;
    if (safariPattern.test(Env.userAgent) && !noSafariPattern.test(Env.userAgent) &&
      !NO_CHROME_PATTERN.test(Env.userAgent) && !Env.isAndroid[0]) {
      return [true, Env.#filterVersion(safariVersionPattern, false)];
    }
    return [false, -1];
  }

  static get isMacosSafari() {
    if (Env.isMacOS && Env.isSafari[0] && !Env.isIpadOS) {
      return [true, Env.isSafari[1]];
    }
    return [false, -1];
  }

  static get isApple() {
    if (Env.isIos[0] || Env.isMacosSafari[0] || Env.isIpadOS) {
      return true;
    }
    return false;
  }

  static get isFirefox() {
    const firefoxPattern = /firefox/i;
    if (firefoxPattern.test(Env.userAgent)) {
      return true;
    }
    return false;
  }

  static get isAndroid() {
    const androidPattern = /android/i;
    const androidVersionPattern = /android\s*(\d+)/i;
    if (Env.isIos[0] || !Env.primaryPointerIsTouchScreen) {
      return [false, -1];
    } else if (androidPattern.test(Env.userAgent)) {
      return [true, Env.#filterVersion(androidVersionPattern, false)];
    }
    return [false, -1];
  }

  static get smartTVData() {
    const tizenPattern = /tizen/i;
    const smartTVSamsungPattern = /smart-tv/i;
    const webosPattern = /(web0s|webos)/i;
    const smartTVLGPattern = /smarttv/i;
    if (tizenPattern.test(Env.userAgent) && smartTVSamsungPattern.test(Env.userAgent)) {
      return { tv: true, webos: false, tizen: true };
    } else if (webosPattern.test(Env.userAgent) && smartTVLGPattern.test(Env.userAgent)) {
      return { tv: true, webos: true, tizen: false };
    }
    return { tv: false, webos: false, tizen: false };
  }

  static get devicePixelRatio() {
    if (typeof window.devicePixelRatio === 'number' && window.devicePixelRatio > 1) {
      return window.devicePixelRatio;
    }
    return 1;
  }

  static get isStandalone() {
    // ios
    if (Env.isIos[0] && typeof navigator.standalone !== 'undefined' && navigator.standalone) {
      return true;
    } else if (Env.isAndroid[0] && typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) {
      // chrome android
      return true;
    }
    return false;
  }

  static get isAndroidMobileOrTabletWebView() {
    const chromePattern = /chrome\/[.0-9]*/i;
    const newWebViewPattern = /wv/;
    const oldWebViewPattern = /version\/[.0-9]*/i;
    if (NO_CHROME_PATTERN.test(Env.userAgent) || !Env.isAndroid[0] || Env.isStandalone) {
      return false;
    }
    if (Env.isAndroid[1] < 5 && oldWebViewPattern.test(Env.userAgent)) {
      return true;
    } else if (Env.isAndroid[1] >= 5 && chromePattern.test(Env.userAgent) && newWebViewPattern.test(Env.userAgent)) {
      return true;
    }
    return false;
  }

  // feature detect
  static get okMp4() {
    if (Env.#html5Video) {
      const canPlayType = TEST_VIDEO.canPlayType('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');
      if (canPlayType !== '') {
        return true;
      }
    }
    return false;
  }

  static get okWebM() {
    if (Env.#html5Video) {
      const canPlayType = TEST_VIDEO.canPlayType('video/webm; codecs="vp9,opus"');
      if (canPlayType !== '') {
        return true;
      }
    }
    return false;
  }

  static get okM4a() {
    if (Env.#html5Audio) {
      const canPlayType = TEST_AUDIO.canPlayType('audio/mp4; codecs="mp4a.40.2"');
      if (canPlayType !== '') {
        return true;
      }
    }
    return false;
  }

  static get okMp3() {
    if (Env.#html5Audio) {
      const canPlayType = TEST_AUDIO.canPlayType('audio/mpeg');
      if (canPlayType !== '') {
        return true;
      }
    }
    return false;
  }

  static get okOgg() {
    if (Env.#html5Audio) {
      const canPlayType = TEST_AUDIO.canPlayType('audio/ogg; codecs="opus"');
      if (canPlayType !== '') {
        return true;
      }
    }
    return false;
  }

  static get okMse() {
    if (Env.#html5Video && typeof MediaSource !== 'undefined' && typeof MediaSource.isTypeSupported === 'function') {
      return true;
    }
    return false;
  }

  static get okMms() {
    if (Env.#html5Video && typeof window.ManagedMediaSource !== 'undefined' &&
      typeof window.ManagedMediaSource.isTypeSupported === 'function') {
      return true;
    }
    return false;
  }

  static get okShaka() {
    if ((Env.okMse || Env.okMms) && typeof Promise !== 'undefined' && typeof Uint8Array !== 'undefined' &&
      typeof Array.prototype.forEach !== 'undefined') {
      return true;
    }
    return false;
  }

  static get okHlsJS() {
    if (Env.okMse || Env.okMms) {
      return true;
    }
    return false;
  }

  static get okHls() {
    if (Env.#html5Video) {
      const isSupp1 = TEST_VIDEO.canPlayType('application/vnd.apple.mpegurl');
      const isSupp2 = TEST_VIDEO.canPlayType('application/x-mpegurl');
      if (isSupp1 !== '' || isSupp2 !== '') {
        return true;
      }
    }
    return false;
  }

  static get okEme() {
    if (typeof window.MediaKeys !== 'undefined' || typeof window.WebKitMediaKeys !== 'undefined' ||
      typeof window.MSMediaKeys !== 'undefined') {
      return true;
    }
    return false;
  }

  static get okFps() {
    if (Env.#html5Video && Env.okEme && ((Env.isMacosSafari[0] && Env.isMacosSafari[1] >= 9) ||
      (Env.isIos[0] && Env.isIos[1] > 10) || Env.isIpadOS)) {
      return true;
    }
    return false;
  }

  static get ok360() {
    const canvas = document.createElement('canvas');
    try {
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch (warning) {
      console.warn(warning);
      try {
        return !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
      } catch (warning2) {
        console.warn(warning2);
        return false;
      }
    }
  }

  static get isInIframe() {
    try {
      if (window.parent !== window) {
        return true;
      }
    } catch (warning) {
      console.warn(warning);
      return false;
    }
    return false;
  }

  static get hasIntersectionObserver() {
    if (typeof IntersectionObserver !== 'undefined') {
      return true;
    }
    return false;
  }

  static get viewportSize() {
    let width = -1;
    let height = -1;
    const currentInnerWidth = (typeof innerWidth === 'number' && innerWidth > 0) ? innerWidth : -1;
    const currentInnerHeight = (typeof innerHeight === 'number' && innerHeight > 0) ? innerHeight : -1;
    if (currentInnerWidth > 0 && currentInnerHeight > 0) {
      width = currentInnerWidth;
      height = currentInnerHeight;
    }
    return {
      width,
      height
    };
  }

  static get hasPassiveEventListeners() {
    let supported = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get: () => {
          supported = true;
          return supported;
        }
      });
      window.addEventListener('testPassive', null, opts);
      window.removeEventListener('testPassive', null, opts);
    } catch (warning) {
      console.warn(warning);
    }
    return supported;
  }

  static get orientation() {
    if (window.matchMedia('(orientation: portrait)').matches) {
      return 'portrait';
    } else {
      return 'landscape';
    }
  }

  static get hasScreenOrientationAPI() {
    if (window.screen.orientation && typeof window.screen.orientation.type === 'string' &&
      typeof window.screen.orientation.addEventListener === 'function') {
      return true;
    }
    return false;
  }

  static get hasScreenOrientationLockAPI() {
    if (window.screen.orientation && typeof window.screen.orientation.type === 'string' &&
      typeof window.screen.orientation.addEventListener === 'function' && 
      typeof window.screen.orientation.lock === 'function') {
      return true;
    }
    return false;
  }


  static get screenDimension() {
    let result = {
      width: -1,
      height: -1
    };
    let width = screen && typeof window.screen.width === 'number' && window.screen.width > 0 ? window.screen.width : -1;
    let height = screen && typeof window.screen.height === 'number' && window.screen.height > 0 ? window.screen.height : -1;
    if (Env.orientation === 'landscape') {
      result.width = Math.max(width, height);
      result.height = Math.min(width, height);
    } else {
      result.width = Math.min(width, height);
      result.height = Math.max(width, height);
    }
    return result;
  }

  static get isOnline() {
    if (typeof navigator.onLine === 'boolean' && navigator.onLine === false) {
      return false;
    }
    return true;
  }

  static get hasDownloadSupport() {
    if (typeof window.shaka !== 'undefined' && window.shaka.offline &&
      window.shaka.offline.Storage && window.shaka.offline.Storage.support()) {
      return true;
    }
    return false;
  }


  static get hasNativeFullscreenSupport() {
    const doc = document.documentElement;
    if (doc) {
      if (typeof doc.requestFullscreen !== 'undefined' ||
        typeof doc.webkitRequestFullscreen !== 'undefined' ||
        typeof doc.mozRequestFullScreen !== 'undefined' ||
        typeof doc.msRequestFullscreen !== 'undefined' ||
        typeof TEST_VIDEO.webkitEnterFullscreen !== 'undefined') {
        return true;
      }
    }
    return false;
  }

  static getEstimateBW(hlsJS) {
    let estimateBW = window.rmpGlobals.rmpConnection.bandwidthData.estimate;
    if (estimateBW > 0) {
      estimateBW = Math.round(estimateBW * 1000 * 1000);
      Logger.print('rmp-connection', 'bandwidthData.estimate found with value ' + estimateBW + ' bps');
    } else {
      if (hlsJS) {
        return -1;
      }
      estimateBW = 700000;
      if (Env.isIos[0] || Env.isAndroid[0] || Env.isIpadOS) {
        estimateBW = 350000;
      }
    }
    return estimateBW;
  }
}
